import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { getJobLog, getSystemJobLog } from '../../services/cloneProvider';
import { getErrorMessage } from './badges';

const LINES_OPTIONS = [50, 100, 200, 500, 1000];
const INTERVAL_OPTIONS = [
  { value: '1s', ms: 1000 },
  { value: '3s', ms: 3000 },
  { value: '5s', ms: 5000 },
  { value: '10s', ms: 10000 },
];

// Vòng xoay đếm ngược tới lần auto-refresh kế tiếp - thuần CSS animation
// (stroke-dashoffset chạy từ 0 -> full trong đúng intervalMs), restart bằng
// cách đổi `key` mỗi cycle, không cần setInterval riêng để tick từng giây.
function RefreshCountdownRing({ intervalMs, cycleKey }) {
  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg key={cycleKey} width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
      <circle cx="9" cy="9" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="2" />
      <circle
        cx="9"
        cy="9"
        r={radius}
        fill="none"
        stroke="#06b6d4"
        strokeWidth="2"
        strokeDasharray={circumference}
        strokeDashoffset={0}
        transform="rotate(-90 9 9)"
        style={{
          animation: `job-log-countdown ${intervalMs}ms linear forwards`,
        }}
      />
      <style>{`
        @keyframes job-log-countdown {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: ${circumference}; }
        }
      `}</style>
    </svg>
  );
}

// 1 panel log cho đúng 1 job (kind+serviceType, hoặc systemName nếu job
// không theo service_type) - tự quản lý lines/autoRefresh/log data riêng,
// không chia sẻ state với panel khác. `expanded` điều khiển từ ngoài
// (JobsTab's toggle on/off) nhưng user vẫn tự bấm mở/đóng độc lập được.
export default function JobLogPanel({ title, kind, serviceType, systemName, expanded, onToggleExpanded }) {
  const [lines, setLines] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState('5s');
  const [logLines, setLogLines] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);

  const fetchLog = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = systemName
        ? await getSystemJobLog(systemName, lines)
        : await getJobLog(kind, serviceType, lines);
      setLogLines(data.lines);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [kind, serviceType, systemName, lines]);

  useEffect(() => {
    if (expanded) fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, fetchLog]);

  const intervalMs = INTERVAL_OPTIONS.find((o) => o.value === refreshInterval)?.ms || 5000;
  // Tăng mỗi khi fetch chạy xong 1 vòng - đổi key để CSS animation (countdown
  // ring) restart đúng lúc, không lệch pha với setInterval thực tế.
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    if (!expanded || !autoRefresh) return undefined;
    setCycleKey((k) => k + 1); // restart ring ngay khi bật/đổi interval
    const timer = setInterval(() => {
      fetchLog();
      setCycleKey((k) => k + 1);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [expanded, autoRefresh, intervalMs, fetchLog]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !shouldStickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [logLines]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 40;
  }

  return (
    <div className="rounded-2xl border border-cyan-100 bg-white">
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <span className="text-xs text-slate-400">{expanded ? '▲ Thu gọn' : '▼ Mở rộng'}</span>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-slate-100 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
            >
              {LINES_OPTIONS.map((n) => <option key={n} value={n}>{n} dòng</option>)}
            </select>

            <label className="flex items-center gap-1.5 text-sm text-slate-700">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
              Tự cập nhật
            </label>

            {autoRefresh && (
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(e.target.value)}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
              >
                {INTERVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
              </select>
            )}

            {autoRefresh && <RefreshCountdownRing intervalMs={intervalMs} cycleKey={cycleKey} />}

            <button
              type="button"
              onClick={fetchLog}
              disabled={isLoading}
              className="ml-auto rounded-lg border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-40"
            >
              Tải lại ngay
            </button>
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-80 overflow-auto rounded-xl border border-slate-700 bg-slate-950 p-3 font-mono text-xs text-slate-200"
          >
            {logLines === null ? (
              <p className="text-slate-500">Đang tải...</p>
            ) : logLines.length === 0 ? (
              <p className="text-slate-500">Chưa có log nào trong ngày hôm nay (UTC).</p>
            ) : (
              <pre className="whitespace-pre-wrap">{logLines.join('\n')}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
